<script setup lang="ts">
import {label} from '../../mixins/translations.js';
import { type Ref, ref} from "vue";
import {useCartStore} from "../../stores/cart";
import {Money} from "../../../utils/money.ts";
import Skeleton from "../Skeleton.vue";
import CartDiscount from "./CartDiscount.vue";
import CartItem from "./CartItem.vue";
import CartPriceLoadingSkeleton from "./CartPriceLoadingSkeleton.vue";

interface Props {
    showDiscountInput?: boolean
}

const props = withDefaults<Props, Props>(defineProps<Props>(), {
    showDiscountInput: true
})

const cartStore = useCartStore();

const mobileShowProducts : Ref<boolean> = ref(false);
</script>

<template>
    <ul role="list" class="divide-y divide-gray-200 text-sm text-gray-800 overflow-auto">
        <template v-if="cartStore.isLoadingFull">
            <li class="flex items-center space-x-4 px-4 py-6 sm:px-6" v-for="n in 3" :key="n">
                <div class="relative flex shrink-0 rounded-md">
                    <Skeleton class="size-20"/>
                </div>
                <div class="ml-6 flex-auto space-y-2">
                    <Skeleton class="w-3/4"/>
                    <Skeleton class="w-2/4"/>
                </div>
                <p class="font-medium flex flex-col text-right space-y-1">
                    <Skeleton class="w-12"/>
                </p>
            </li>
        </template>
        <template v-else>
            <div :class="{ 'hidden': mobileShowProducts }" class="md:hidden flex justify-between text-md font-bold text-gray-800 p-4 hover:bg-gray-100 cursor-pointer" @click="mobileShowProducts = true">
                <span class="flex flex-1" v-html="label('site.cart.mobile.show_products')"></span>
                <span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5"><path d="m6 9 6 6 6-6"/></svg>
                </span>
            </div>
            <CartItem :class="{ 'flex!': mobileShowProducts }" class="hidden md:flex" :item="item" v-for="item of cartStore.items"></CartItem>
        </template>
    </ul>

    <dl class="space-y-6 border-t border-gray-200 py-6 px-4 sm:px-6" v-if="!cartStore.isLoadingFull && cartStore.cart">
        <div class="flex items-center justify-between">
            <dt class="text-sm">{{ label('site.cart.sub_total') }}</dt>
            <dd class="text-sm font-medium text-gray-800">
                <CartPriceLoadingSkeleton>{{ Money.format(cartStore.cart.sub_total_excl, cartStore.cart?.currency) }}</CartPriceLoadingSkeleton>
            </dd>
        </div>

        <div class="flex items-center justify-between">
            <dt class="text-sm">{{ label('site.cart.delivery_costs') }}</dt>
            <dd class="text-sm font-medium text-gray-800">
                <CartPriceLoadingSkeleton>{{ Money.format(cartStore.cart.delivery_cost_incl ?? 0, cartStore.cart?.currency) }}</CartPriceLoadingSkeleton>
            </dd>
        </div>
        <div class="flex items-center justify-between" v-for="(value, rate) in cartStore.cart?.taxes">
            <dt class="text-sm">{{ label('site.cart.vat') }} {{ rate }}%</dt>
            <dd class="text-sm font-medium text-gray-800">
                <CartPriceLoadingSkeleton>{{ Money.format(value, cartStore.cart.currency) }}</CartPriceLoadingSkeleton>
            </dd>
        </div>
        <div class="space-y-1">
            <div class="flex items-center justify-between" v-if="cartStore.cart.discount_excl">
                <dt class="text-sm">
                    {{ label('site.cart.discount_code') }}
                </dt>
                <dd class="text-sm font-medium text-gray-800">
                    <CartPriceLoadingSkeleton>{{ Money.format(cartStore.cart.discount_excl, cartStore.cart.currency) }}</CartPriceLoadingSkeleton>
                </dd>
            </div>
            <div class="text-sm pl-4" v-if="showDiscountInput">
                <CartDiscount :discount_models="cartStore.cart?.discount_models" />
            </div>
        </div>
        <div class="flex items-center justify-between border-t border-gray-200 pt-6">
            <dt class="text-base font-medium">{{ label('site.cart.total') }}</dt>
            <dd class="text-base font-medium text-gray-800">
                <CartPriceLoadingSkeleton>{{ Money.format(cartStore.cart.total, cartStore.cart.currency) }}</CartPriceLoadingSkeleton>
            </dd>
        </div>
    </dl>

    <div class="border-t border-gray-200 py-6 px-4 sm:px-6" v-if="$slots.actions">
        <slot name="actions"></slot>
    </div>

</template>
